// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Durable memory records and retention.
 *
 * `MEM-047` and `MEM-015` bound what memory may contain and require retention
 * to be explicit. A record without an expiry is therefore flagged, because
 * indefinite retention is a prohibited practice rather than a neutral default.
 * Record content is shown truncated: administration needs to identify a record,
 * not to read protected content in bulk (`DAT-006` minimisation).
 */

import { capabilities, queryKeys, StateBadge, type ValidatedMemoryRecord } from '@eai/ui-shared';
import type { ReactNode } from 'react';

import { ResourceScreen, useControlPlaneQuery } from '../app-shell';

/** Maximum characters of record content shown in the table. */
export const CONTENT_PREVIEW_LIMIT = 120;

/** Truncate record content to the administration preview bound. */
export function previewContent(content: string): string {
  return content.length <= CONTENT_PREVIEW_LIMIT
    ? content
    : `${content.slice(0, CONTENT_PREVIEW_LIMIT)}…`;
}

/** Durable memory screen. */
export function MemoryScreen(): ReactNode {
  const query = useControlPlaneQuery(
    (scope) => queryKeys.controlPlane.memoryRecords(scope),
    (client, signal) => client.listMemoryRecords({ signal }),
  );

  return (
    <ResourceScreen<ValidatedMemoryRecord>
      title="Memory"
      description="Durable memory records with their retention. Content is previewed only; a record without an expiry is flagged because indefinite retention is not permitted."
      requiredCapability={capabilities.memoryRead}
      area="Memory"
      query={query}
      rowKey={(row) => row.memory_id}
      emptyTitle="No durable memory record exists"
      emptyDescription="Memory is created only through an approved admission path, so an empty list is a normal state."
      columns={[
        { key: 'kind', header: 'Kind', render: (row) => row.kind },
        { key: 'subject', header: 'Subject', render: (row) => row.subject_id ?? 'Not reported' },
        { key: 'content', header: 'Content preview', render: (row) => previewContent(row.content) },
        { key: 'created', header: 'Created', render: (row) => row.created_at ?? 'Not reported' },
        {
          key: 'expiry',
          header: 'Expiry',
          render: (row) =>
            row.expires_at ?? (
              <StateBadge label="No expiry set" tone="caution" subject="Memory retention" />
            ),
        },
      ]}
    />
  );
}
